import type { FC, CSSProperties } from 'react'
import { useRef, useCallback } from 'react'

import { isAtEnd, isAtStart } from '@/shared/lib/selection-caret'

import type { Cue } from '~shared/rundown/cue'

import { HEADING_COLUMN_ID } from '@/entities/rundown'

import {
  keyboardNavigationEventBus,
  KeyboardNavigationEvent,
  KeyboardNavigationDirection,
  KeyboardNavigationInput,
} from '@/features/keyboard-navigation-handler'
import {
  cueUiActions,
  useIsGroupCollapsed,
} from '@/features/rundown/cue/cue-ui.store'

import { CueTitle } from '../CueTitle'

import type { GroupChildren } from './group.types'
import { GroupCollapseButton } from './GroupCollapseButton'
import { GroupPlannedTimings } from './GroupPlannedTimings'
import { GroupRunTimings } from './GroupRunTimings'

type GroupProps = {
  cue: Cue
  childrenCues: GroupChildren
  readonly?: boolean

  onUpdateCue?: (value?: string) => void | Promise<void>
  style?: CSSProperties
}

export const Group: FC<GroupProps> = ({
  cue,
  childrenCues,
  readonly,
  onUpdateCue,
  style,
}) => {
  // Read our own collapsed flag here instead of taking it as a prop from
  // CueItem. That keeps CueItem from subscribing to the collapsed-groups
  // array (which would re-render every cue in the rundown on every toggle).
  const collapsed = useIsGroupCollapsed(cue.id)

  const titleRef = useRef(null)

  const handleGroupTitleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const el = titleRef.current as HTMLDivElement | null
      const atStart = el ? isAtStart(el) : false
      const atEnd = el ? isAtEnd(el) || el.innerText.trim().length === 0 : false

      const prevent = () => {
        event.preventDefault()
        event.stopPropagation()
      }

      // Tab handling maps to FORWARD/REVERSE (distinct from arrow keys)
      if (event.key === 'Tab') {
        prevent()
        keyboardNavigationEventBus.emit(KeyboardNavigationEvent.JUMP, {
          direction: event.shiftKey
            ? KeyboardNavigationDirection.REVERSE
            : KeyboardNavigationDirection.FORWARD,
          fromCueId: cue.id,
          fromColumnId: HEADING_COLUMN_ID,
          fromInputType: KeyboardNavigationInput.TITLE,
        })
        return
      }

      if (event.key === 'ArrowDown' && atEnd) {
        prevent()
        keyboardNavigationEventBus.emit(KeyboardNavigationEvent.JUMP, {
          direction: KeyboardNavigationDirection.DOWN,
          fromCueId: cue.id,
          fromColumnId: HEADING_COLUMN_ID,
          fromInputType: KeyboardNavigationInput.TITLE,
        })
        return
      }

      if (event.key === 'ArrowUp' && atStart) {
        prevent()
        keyboardNavigationEventBus.emit(KeyboardNavigationEvent.JUMP, {
          direction: KeyboardNavigationDirection.UP,
          fromCueId: cue.id,
          fromColumnId: HEADING_COLUMN_ID,
          fromInputType: KeyboardNavigationInput.TITLE,
        })
        return
      }

      if (event.key === 'ArrowRight' && atEnd) {
        prevent()
        keyboardNavigationEventBus.emit(KeyboardNavigationEvent.JUMP, {
          direction: KeyboardNavigationDirection.RIGHT,
          fromCueId: cue.id,
          fromColumnId: HEADING_COLUMN_ID,
          fromInputType: KeyboardNavigationInput.TITLE,
        })
        return
      }
    },
    [cue.id],
  )

  return (
    <div
      className="relative overflow-hidden flex flex-row justify-between gap-0 w-[558px]"
      style={style}
    >
      <div className="w-full h-full flex flex-col justify-between">
        <div className="flex flex-col h-full justify-center">
          <div>
            <CueTitle
              value={cue.title}
              inputRef={titleRef}
              className="font-semibold px-4"
              onChange={onUpdateCue}
              readonly={readonly || cue.settings?.preventEdits}
              onKeyDown={handleGroupTitleKeyDown}
            />
          </div>

          <GroupPlannedTimings childrenCues={childrenCues} />
        </div>
        <GroupRunTimings childrenCues={childrenCues} />
      </div>
      <div className="absolute top-1/2 -translate-y-1/2 right-0 shrink-0 flex flex-col justify-center mr-2">
        <GroupCollapseButton
          onClick={() => cueUiActions.toggleGroup(cue.id)}
          childrenCount={childrenCues.length}
          collapsed={collapsed}
        />
      </div>
    </div>
  )
}
